"use client";

import React, { useState, useRef, useContext, createContext, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import ProcessingOverlay from "@/components/ProcessingOverlay";
import BeforeAfterPreview from "@/components/BeforeAfterPreview";
import { useCreditsContext } from "@/contexts/CreditsContext";
import { ToolInfo } from "@/contexts/ToolsContext";
import { useEditorHistory } from "@/hooks/useEditorHistory";
import { generateImageId, generateImageFilename } from "@/utils/imageUtils";
import Icon from "@/components/Icon";
import ImageDimensions from "./ImageDimensions";
import FreeUserConversionModal from "@/components/FreeUserConversionModal";
import { clearGallerySession } from "@/libs/editor-image-storage";
import GalleryCarousel from "./GalleryCarousel";
import GalleryZoomModal from "./GalleryZoomModal";
import SaveButton, { galleryActionPillClassName } from "./SaveButton";
import PhotoGuideModal from "@/components/PhotoGuideModal";
import { isAiReportToolId } from "@/libs/ai-report-tools";
import { fileToUploadDataUrl } from "@/libs/client-image-upload";

// Context for sharing state between config panel and scene
interface EditorContextType {
  originalImage: string | null;
  setOriginalImage: (image: string | null) => void;
  processedImage: string | null;
  setProcessedImage: (image: string | null, fileId?: string) => Promise<void>;
  setProcessedImageInternal?: (image: string | null) => void; // Non-tracking version for internal use
  isProcessing: boolean;
  setIsProcessing?: (processing: boolean) => void;
  isUploading?: boolean; // True while a source image is being uploaded/loaded
  setIsUploading?: (uploading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  handleGenerate: () => Promise<void>;
  getCurrentImage: () => string | null; // Helper to get the current working image
  hasProcessedImage: () => boolean; // Helper to check if there's a processed version
  trackPhotoUpdate: (imageData: string) => void; // Track photo updates in history
  isHistoryEnabled: boolean; // Whether history tracking is enabled for this tool
  isLoadingRefineImage?: boolean; // Whether image is being loaded from refine action
  customDownloadHandler?: () => void; // Custom download handler for specific tools
  setCustomDownloadHandler: (handler: (() => void) | undefined) => void; // Set custom download handler
  handleBackNavigation?: () => void; // Handler for back button clicks

  // Gallery state (for dev tools with unlimited generations)
  galleryImages?: Array<{ imageId: string; imageData: string; timestamp: number; isProcessing?: boolean }>;
  currentGalleryIndex?: number;
  setCurrentGalleryIndex?: (index: number) => void;
  isGalleryEnabled?: boolean; // Whether gallery is enabled for this tool
  clearGallery?: () => Promise<void>; // Clear all gallery images for current session

  /** When true, v2 shells should mount SharedImageScene (result / processing pane). */
  showResultPane?: boolean;
  setShowResultPane?: (show: boolean) => void;

  // Processed image fileId (for save button in non-gallery mode)
  processedImageFileId?: string | null;

  // Saved images tracking (in-memory for current session)
  savedImageIds?: Set<string>;
  setSavedImageIds?: React.Dispatch<React.SetStateAction<Set<string>>>;

  // Enhancement state (for tools with optional post-processing)
  enhancementEnabled?: boolean;
  setEnhancementEnabled?: (enabled: boolean) => void;
}

export const EditorContext = createContext<EditorContextType | null>(null);

export function useEditor() {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditor must be used within EditorContext");
  }
  return context;
}

interface SharedImageSceneProps {
  toolInfo: ToolInfo;
}

export default function SharedImageScene({ toolInfo }: SharedImageSceneProps) {
  const [showPrevious, setShowPrevious] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [hasDownloadedImage, setHasDownloadedImage] = useState(false);
  const [showFreeUserModal, setShowFreeUserModal] = useState(false);
  const [showAiEditorModal, setShowAiEditorModal] = useState(false);
  const [showPhotoGuideModal, setShowPhotoGuideModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // TODO: Set back to false after layout testing
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const isAiPhotoshootTool = toolInfo.id === 'ai-avatars' || toolInfo.id === 'ai-avatars-dev';
  const canEditWithAi =
    toolInfo.id !== "ai-designer" && !isAiReportToolId(toolInfo.id);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const router = useRouter();
  const { credits, uqf, refreshCredits } = useCreditsContext();

  // Get state from context
  const {
    originalImage,
    setOriginalImage,
    processedImage,
    setProcessedImage,
    setProcessedImageInternal,
    isProcessing,
    setIsProcessing,
    error,
    setError,
    isHistoryEnabled,
    isLoadingRefineImage,
    customDownloadHandler,
    galleryImages,
    currentGalleryIndex,
    setCurrentGalleryIndex,
    isGalleryEnabled,
    clearGallery,
    processedImageFileId,
    savedImageIds,
    setSavedImageIds,
    enhancementEnabled,
  } = useEditor();

  // History integration
  const historyHook = useEditorHistory();

  // Helper to get the previous image in history OR original image for non-history tools
  const getPreviousImage = () => {
    // Gallery mode: show original image (LAST item in gallery array)
    if (isGalleryEnabled && galleryImages && galleryImages.length > 0) {
      const lastImage = galleryImages[galleryImages.length - 1];
      return lastImage?.imageData || null;
    }
    // For tools without history: if we have a processed image, show original as "previous"
    if (!isHistoryEnabled && processedImage && originalImage) {
      return originalImage;
    }
    // For tools with history: use history
    if (historyHook.history.currentIndex <= 0) return null;
    return historyHook.history.actions[historyHook.history.currentIndex - 1]?.imageData || null;
  };

  // Helper to get the current image being displayed (considering history navigation and gallery)
  const getCurrentDisplayedImage = () => {
    // Gallery mode (for dev tools)
    if (isGalleryEnabled && galleryImages && galleryImages.length > 0) {
      const safeIndex = Math.min(currentGalleryIndex || 0, galleryImages.length - 1);
      const currentImage = galleryImages[safeIndex];
      // Skip processing placeholders
      if (currentImage?.isProcessing) {
        return originalImage;
      }
      return currentImage?.imageData || originalImage;
    }

    // History mode (for AI Designer)
    if (processedImage) return processedImage; // Latest generated image takes priority
    if (historyHook.history.currentIndex > 0) {
      return historyHook.history.actions[historyHook.history.currentIndex]?.imageData || originalImage;
    }
    return originalImage;
  };

  // Helper to determine if we can show previous
  const canShowPrevious = () => {
    // Gallery mode: can show previous if we have at least 2 images in gallery (original + generated)
    if (isGalleryEnabled && galleryImages && galleryImages.length > 1) {
      return true;
    }
    // For tools with history disabled: can show previous if we have both original and processed images
    if (!isHistoryEnabled) {
      return !!(originalImage && processedImage);
    }
    // For tools with history enabled: use history
    return historyHook.history.currentIndex > 0;
  };

  // File processing
  const processFile = async (file: File) => {
    // Remove frontend size limits - let server handle all validation
    // Only basic type validation for better UX
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif'];
    const type = (file.type || '').toLowerCase();
    const looksAvif = file.name.toLowerCase().endsWith('.avif');
    if (!validTypes.includes(type) && !(looksAvif && (!type || type === 'application/octet-stream'))) {
      setError('Please upload a valid image file (PNG, JPG, WEBP, AVIF)');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // AVIF → JPEG data URL so server Sharp always gets a supported format
      const imageData = await fileToUploadDataUrl(file);

      let imageUrl = imageData;
      let fileId = `guest-${Date.now()}`; // Default guest fileId

      try {
        // Upload to S3 directly (no client-side processing)
        const uploadResponse = await fetch('/api/storage/upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: imageData,
            filename: looksAvif ? file.name.replace(/\.avif$/i, '.jpg') : file.name,
          }),
        });

        if (uploadResponse.ok) {
          // Authenticated user: use S3 uploaded image
          const uploadResult = await uploadResponse.json();

          // Preload image before showing it (smooth experience, no flicker)
          await new Promise<void>((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load uploaded image'));
            img.src = uploadResult.signedUrl;
          });

          imageUrl = uploadResult.signedUrl;
          fileId = uploadResult.fileId;
          console.log('✅ Image uploaded to S3:', fileId);
        } else if (uploadResponse.status === 401) {
          // Guest user: use client-side base64 data URL
          console.log('📷 Guest user: using client-side image for free tool');
          // imageUrl already contains base64 data URL
        } else {
          // Other errors: throw
          const errorData = await uploadResponse.json();
          throw new Error(errorData.error || 'Upload failed');
        }
      } catch (uploadError) {
        // Network error or other issues: fall back to client-side for guests
        console.warn('Upload failed, using client-side processing:', uploadError);
      }

      // Clear history for ai-designer (fresh start with new upload)
      if (isHistoryEnabled) {
        historyHook.clearHistory();
        // Reset refine file ID tracking (in case we came from another tool)
        if (typeof window !== 'undefined') {
          (window as any).__lastProcessedRefineFileId = null;
        }
      }

      // Store fileId and display image (either S3 URL or base64 data URL)
      setOriginalImage(imageUrl);
      (window as any).__uploadedFileId = fileId; // Store fileId globally for tool access
      setProcessedImageInternal?.(null);

      // Track photo upload in history only if history is enabled
      if (isHistoryEnabled) {
        historyHook.trackPhotoUpload(imageUrl, fileId);
      }

      // Refresh assets list to include the newly uploaded image (only for authenticated users)
      if (typeof window !== 'undefined' && !fileId.startsWith('guest-')) {
        window.dispatchEvent(new CustomEvent('refreshAssets'));
      }
    } catch (error) {
      console.error('Failed to process image:', error);
      setError(error instanceof Error ? error.message : 'Failed to process image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  // Handler for upload box click - check if user has credits
  const handleUploadBoxClick = () => {
    // If user has no credits (0), show conversion modal instead of file dialog
    if (credits !== null && credits < 1) {
      setShowFreeUserModal(true);
      return;
    }

    // User has credits, allow normal upload
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    // If user has no credits, show conversion modal instead of processing
    if (credits !== null && credits < 1) {
      setShowFreeUserModal(true);
      return;
    }

    // Check if dropping an asset from AssetsPanel
    const assetFileId = e.dataTransfer.getData('assetFileId');
    const assetSignedUrl = e.dataTransfer.getData('assetSignedUrl');

    if (assetFileId && assetSignedUrl) {
      // Dropping asset from panel
      try {
        // Preload image
        await new Promise<void>((resolve, reject) => {
          const img = new window.Image();
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = assetSignedUrl;
        });

        setOriginalImage(assetSignedUrl);
        (window as any).__uploadedFileId = assetFileId;
        setProcessedImageInternal?.(null);
        setError(null);

        console.log('✅ Asset loaded via drag-drop:', assetFileId);
      } catch (error) {
        console.error('Failed to load dropped asset:', error);
        setError('Failed to load image');
      }
      return;
    }

    // Otherwise, handle normal file drop
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        processFile(file);
      } else {
        setError('Please upload a valid image file (PNG, JPG, WEBP, or SVG)');
      }
    }
  };

  useEffect(() => {
    const handleUploadRequest = () => {
      handleUploadBoxClick();
    };

    const handleUploadFileRequest = (event: Event) => {
      const customEvent = event as CustomEvent<File>;
      const file = customEvent.detail;
      if (file instanceof File) {
        processFile(file);
      }
    };

    const handleOpenPhotoGuideRequest = () => {
      setShowPhotoGuideModal(true);
    };

    window.addEventListener('editor:request-image-upload', handleUploadRequest);
    window.addEventListener('editor:upload-image-file', handleUploadFileRequest as EventListener);
    window.addEventListener('editor:open-photo-guide', handleOpenPhotoGuideRequest);

    return () => {
      window.removeEventListener('editor:request-image-upload', handleUploadRequest);
      window.removeEventListener('editor:upload-image-file', handleUploadFileRequest as EventListener);
      window.removeEventListener('editor:open-photo-guide', handleOpenPhotoGuideRequest);
    };
  }, [credits]);

  // iOS Safari detection
  const isIOSSafari = () => {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    return isIOS && isSafari;
  };

  // Mobile detection (for non-iOS mobile)
  const isMobile = () => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // SYNCHRONOUS data URL to Blob conversion (no async, preserves user gesture)
  const dataURLtoBlob = (dataURL: string): Blob => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(parts[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleDownload = async () => {
    // Use custom download handler if available (for tools like add-logo-watermark)
    if (customDownloadHandler) {
      setIsDownloading(true);
      customDownloadHandler();
      setTimeout(() => setIsDownloading(false), 2000);
      return;
    }

    // Show downloading state
    setIsDownloading(true);

    // Default download logic for other tools
    // Get the image to download based on mode (gallery, history, or simple)
    let imageToDownload: string | null = null;

    if (isGalleryEnabled && galleryImages && galleryImages.length > 0) {
      // Gallery mode: download the current gallery image
      imageToDownload = galleryImages[currentGalleryIndex || 0]?.imageData || null;
    } else if (!isHistoryEnabled) {
      // Non-history tools: download the processed image
      imageToDownload = processedImage;
    } else {
      // History-enabled tools: download current history item (never the original upload)
      imageToDownload = historyHook.history.currentIndex > 0
        ? historyHook.history.actions[historyHook.history.currentIndex]?.imageData || null
        : null;
    }

    if (!imageToDownload) return;

    // Get the current image ID for consistent filename
    let currentImageId;
    if (isGalleryEnabled && galleryImages && galleryImages.length > 0) {
      currentImageId = galleryImages[currentGalleryIndex || 0]?.imageId;
    } else {
      currentImageId = historyHook.getCurrentImageId();
    }

    const filename = currentImageId
      ? generateImageFilename(currentImageId, toolInfo.id)
      : `pfpmaker_result.png`;

    // ALL PLATFORMS: Use download proxy to ensure proper download behavior
    // (Direct S3 URLs may open in browser instead of downloading)
    try {
      const response = await fetch('/api/download/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          imageUrl: imageToDownload, // Send signed URL to proxy
          filename
        })
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      // Safe removal - check if still a child before removing
      if (link.parentNode === document.body) {
        document.body.removeChild(link);
      }
      setTimeout(() => URL.revokeObjectURL(url), 100);
      setHasDownloadedImage(true);
      setTimeout(() => setIsDownloading(false), 1500);
    } catch (error) {
      console.error('Desktop download failed:', error);
      setError('Download failed. Please try again.');
      setIsDownloading(false);
    }
  };

  // Prevent default drag behavior on the whole page
  useEffect(() => {
    const preventDefaults = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, false);
    });

    return () => {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, false);
      });
    };
  }, []);

  // Listen for "Use Asset" event from AssetsPanel
  useEffect(() => {
    const handleUseAsset = async (e: CustomEvent) => {
      const { fileId, signedUrl } = e.detail;
      await loadAssetFile(fileId, signedUrl);
    };

    window.addEventListener('useAssetFile', handleUseAsset as EventListener);
    return () => {
      window.removeEventListener('useAssetFile', handleUseAsset as EventListener);
    };
  }, [setOriginalImage, setProcessedImageInternal, setError, clearGallery, isGalleryEnabled]);

  // Function to load asset file
  const loadAssetFile = async (fileId: string, signedUrl: string) => {
    // Show loading overlay
    setIsLoadingAsset(true);

    // Preload image
    try {
      await new Promise<void>((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = signedUrl;
      });

      // Clear gallery if it exists
      if (isGalleryEnabled && clearGallery) {
        await clearGallery();
      }

      // Clear history for ai-designer (fresh start with new image)
      if (isHistoryEnabled) {
        historyHook.clearHistory();
        // Reset refine file ID tracking (in case we came from another tool)
        if (typeof window !== 'undefined') {
          (window as any).__lastProcessedRefineFileId = null;
        }
      }

      // Clear processed image first to ensure clean state
      setProcessedImageInternal?.(null);
      setProcessedImage(null);

      // Small delay to ensure state is cleared before setting new image
      await new Promise(resolve => setTimeout(resolve, 50));

      // Set as original image (even if it's the same URL, this ensures state is refreshed)
      setOriginalImage(null); // Clear first
      await new Promise(resolve => setTimeout(resolve, 50));
      setOriginalImage(signedUrl);
      (window as any).__uploadedFileId = fileId;
      setError(null);

      // Track in history for ai-designer (as FIRST action after clearing)
      if (isHistoryEnabled) {
        const imageId = generateImageId(signedUrl);
        historyHook.trackPhotoUpload(signedUrl, imageId);
      }
    } catch (error) {
      console.error('Failed to load asset:', error);
      setError('Failed to load image from library');
    } finally {
      // Hide loading overlay
      setIsLoadingAsset(false);
    }
  };

  // Reset download flag when processed image changes
  useEffect(() => {
    if (processedImage) {
      setHasDownloadedImage(false);
    }
  }, [processedImage]);

  // Expose navigation warning functions to header back button
  useEffect(() => {
    (window as any).__editorHasUnsavedImage = () => processedImage && !hasDownloadedImage;
    (window as any).__editorShowNavigationWarning = () => setShowNavigationWarning(true);

    return () => {
      delete (window as any).__editorHasUnsavedImage;
      delete (window as any).__editorShowNavigationWarning;
    };
  }, [processedImage, hasDownloadedImage]);

  // Warn user before navigation if they have undownloaded images
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check bypass flag first
      if (sessionStorage.getItem('_bypass_warning') === 'true') {
        sessionStorage.removeItem('_bypass_warning');
        return;
      }

      if (processedImage && !hasDownloadedImage) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [processedImage, hasDownloadedImage]);

  // Intercept browser back button
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (processedImage && !hasDownloadedImage) {
        // Push state back to stay on current page
        window.history.pushState(null, '', window.location.href);
        setShowNavigationWarning(true);
      }
      // If no unsaved image, let navigation proceed normally
    };

    // Only add listener if there's an unsaved image
    if (processedImage && !hasDownloadedImage) {
      // Push a dummy state to enable popstate detection
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [processedImage, hasDownloadedImage]);

  // This component just renders the scene (context is provided by the editor page)
  return (
        <div className="h-full w-full min-h-0 bg-white flex flex-col relative overflow-hidden touch-pan-y" style={{
        backgroundImage: 'radial-gradient(#f7f7f7 2px, transparent 0)',
        backgroundSize: '20px 20px',
        WebkitOverflowScrolling: 'touch'
      }}>
        {/* Viewer Toolbar — compact on mobile so freed height goes to config, not scene */}
        <div className="flex flex-shrink-0 bg-white border-b border-gray-200 px-1.5 md:px-4 py-1 md:py-2 items-center justify-between">
          {/* Mobile Burger Button - Only visible on mobile */}
          <button
            data-mobile-burger
            onClick={() => {
              window.dispatchEvent(new CustomEvent('toggleMobileSidebar'));
            }}
            className="md:hidden p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-600"
            title="Menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="hidden md:block"></div> {/* Empty left side on desktop */}

          <div className="flex items-center gap-2.5 md:gap-2">
            {/* Preview Original/Current Button */}
            <button
              onMouseDown={() => setShowPrevious(true)}
              onMouseUp={() => setShowPrevious(false)}
              onMouseLeave={() => setShowPrevious(false)}
              disabled={!canShowPrevious()}
              className={`p-1 md:p-2 rounded-lg transition-colors ${
                !canShowPrevious()
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Hold to view original photo"
            >
              <Icon name="editor-preview" className="w-5 h-5 md:w-8 md:h-8" />
            </button>

            {/* History Controls - Only show when history is enabled (AI Designer) */}
            {isHistoryEnabled && (
              <>
                <button
                  onClick={() => {
                    const previousImage = historyHook.undoAndGetImage();
                    if (previousImage) {
                      // Check if going back to original upload
                      if (historyHook.history.currentIndex === 0) {
                        setOriginalImage(previousImage);
                        setProcessedImageInternal?.(null);
                      } else {
                        setProcessedImageInternal?.(previousImage);
                      }
                    }
                  }}
                  disabled={!historyHook.canUndo}
                  title="Undo"
                  className={`p-1 md:p-2 rounded-lg transition-colors ${
                    !historyHook.canUndo
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon name="editor-undo" className="w-5 h-5 md:w-8 md:h-8" />
                </button>

                <button
                  onClick={() => {
                    const nextImage = historyHook.redoAndGetImage();
                    if (nextImage) {
                      // Check if going to original upload
                      if (historyHook.history.currentIndex === 0) {
                        setOriginalImage(nextImage);
                        setProcessedImageInternal?.(null);
                      } else {
                        setProcessedImageInternal?.(nextImage);
                      }
                    }
                  }}
                  disabled={!historyHook.canRedo}
                  title="Redo"
                  className={`p-1 md:p-2 rounded-lg transition-colors ${
                    !historyHook.canRedo
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon name="editor-redo" className="w-5 h-5 md:w-8 md:h-8" />
                </button>
              </>
            )}

            {/* Reset to Original Button */}
            <button
              onClick={() => setShowClearConfirmModal(true)}
              disabled={!originalImage && !processedImage}
              className={`p-1 md:p-2 rounded-lg transition-colors ${
                !originalImage && !processedImage
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Remove uploaded photo"
            >
                <Icon name="editor-trash" className="w-5 h-5 md:w-8 md:h-8" />
            </button>

            {/* Separator */}
            <div className="w-px h-5 md:h-8 bg-gray-200 mx-1 md:mx-0"></div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              disabled={
                customDownloadHandler
                  ? false  // Custom handler determines its own availability
                  : isGalleryEnabled
                    ? !galleryImages || galleryImages.length === 0  // Gallery mode: need at least one image
                  : !isHistoryEnabled
                    ? !processedImage  // Non-history tools: need processed image
                    : historyHook.history.currentIndex <= 0  // History tools: need history beyond upload
              }
              className={`px-3 md:px-24 py-1.5 md:py-3 rounded-md md:rounded-lg font-medium transition-colors text-sm md:text-base ${
                customDownloadHandler
                  ? 'bg-[#69c85a] text-white hover:bg-opacity-90'  // Always active with custom handler
                  : (isGalleryEnabled ? !galleryImages || galleryImages.length === 0 : !isHistoryEnabled ? !processedImage : historyHook.history.currentIndex <= 0)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#69c85a] text-white hover:bg-opacity-90'
              }`}
            >
              Download
            </button>
          </div>
        </div>

        {/* Image Display Area — min-h-0 so gallery cannot overflow past available flex space */}
        <div className="flex-1 min-h-0 overflow-hidden flex items-center justify-center p-2 md:p-8 relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />

          {/* Error Overlay */}
          {error && !isProcessing && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 p-8">
              <div className="bg-white rounded-lg p-6 max-w-md shadow-xl">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Error</h3>
                    <p className="mt-1 text-sm text-gray-600">{error}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setError(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {!originalImage ? (
            <div className="w-full max-w-2xl">
              {error && !isProcessing && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {!isAiPhotoshootTool && (
                <>
                  {toolInfo.id !== 'ai-headshots' && (
                    <div className="flex justify-center mb-8">
                      <button
                        onClick={() => setShowPhotoGuideModal(true)}
                        className="px-5 py-3 text-sm font-medium text-primary bg-white border-2 border-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                      >
                        <span>How to get best results</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-4 h-4"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  )}

                  {toolInfo.id === 'ai-headshots' && (
                    <div className="mb-6">
                      <div className="flex items-center gap-[0.15rem] md:gap-3">
                        <div className="relative flex-shrink-0">
                          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm bg-[#7A7B77]">
                            <Image src="/assets/images/tools/before.png" alt="Your selfie" fill className="object-cover" sizes="96px" />
                          </div>
                          <p className="absolute left-0 right-0 -bottom-5 text-[11px] text-gray-500 font-medium text-center whitespace-nowrap">Your selfie</p>
                        </div>

                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-primary flex-shrink-0">
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>

                        <div className="relative min-w-0 flex-1">
                          <div
                            className="flex gap-2 overflow-x-auto cursor-grab active:cursor-grabbing select-none"
                            style={{ msOverflowStyle: 'none', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
                            ref={(el) => {
                              if (!el) return;
                              (el as any).style.setProperty('-webkit-overflow-scrolling', 'touch');
                              const sheet = el.ownerDocument.getElementById('hs-strip-style') || (() => {
                                const s = el.ownerDocument.createElement('style');
                                s.id = 'hs-strip-style';
                                s.textContent = '[data-hs-strip]::-webkit-scrollbar{display:none}';
                                el.ownerDocument.head.appendChild(s);
                                return s;
                              })();
                              el.setAttribute('data-hs-strip', '');
                            }}
                            onDragStart={(e) => e.preventDefault()}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const el = e.currentTarget;
                              const startX = e.clientX;
                              const startScroll = el.scrollLeft;
                              const onMove = (ev: MouseEvent) => { el.scrollLeft = startScroll - (ev.clientX - startX); };
                              const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                              document.addEventListener('mousemove', onMove);
                              document.addEventListener('mouseup', onUp);
                            }}
                          >
                            {[
                              "/assets/images/headshots/balzer.jpeg",
                              "/assets/images/headshots/white_shirt.jpeg",
                              "/assets/images/headshots/blazer-2.jpeg",
                              "/assets/images/headshots/bturtle.jpeg",
                              "/assets/images/headshots/suittie.jpeg",
                              "/assets/images/headshots/bcasual.jpeg",
                              "/assets/images/headshots/blshirt.jpeg",
                              "/assets/images/headshots/bshirt.jpeg",
                            ].map((src, i) => (
                              <div key={i} className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden flex-shrink-0 border-2 border-gray-200 shadow-sm">
                                <Image src={src} alt="" fill className="object-cover pointer-events-none" sizes="96px" draggable={false} />
                              </div>
                            ))}
                          </div>
                          <div className="pointer-events-none absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-gray-50 to-transparent" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleUploadBoxClick}
                    className={`
                      relative bg-primary bg-opacity-15 border-2 border-dashed rounded-lg p-8 md:p-24 cursor-pointer
                      transition-all duration-200 hover:bg-primary hover:bg-opacity-35
                      flex flex-col items-center justify-center
                      ${isDragging
                        ? 'border-primary bg-primary scale-[1.02]'
                        : 'border-primary'
                      }
                    `}
                  >
                    <div className="flex items-center justify-center mb-3 md:mb-4">
                      <Icon name="icon-line-upload" className="w-16 h-16 md:w-20 md:h-20 text-primary flex items-center justify-center" />
                    </div>

                    <p className="text-base md:text-lg font-bold text-primary text-center">
                      {credits !== null && credits < 1 ? "Upload Your Photo" : "Upload Your Photo to Start"}
                    </p>

                    <p className="text-xs md:text-sm text-primary mt-1 md:mt-2 text-center">
                      {credits !== null && credits < 1 ? "See plans & pricing to unlock this tool" : "PNG, JPG, WEBP, AVIF, SVG - Any size"}
                    </p>

                    {credits !== null && credits < 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowFreeUserModal(true);
                        }}
                        className="mt-4 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-bold text-sm hover:from-orange-600 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                      >
                        Get Started →
                      </button>
                    )}

                    {isDragging && (
                      <div className="absolute inset-0 bg-primary bg-opacity-100 rounded-lg flex items-center justify-center pointer-events-none">
                        <p className="text-lg font-medium text-white">Drop your image here</p>
                      </div>
                    )}

                    {isUploading && (
                      <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center pointer-events-none">
                        {/*}
                        <svg className="animate-spin h-12 w-12 text-primary mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        */}
                        <p className="text-base font-semibold text-gray-900">Uploading...</p>
                        <div className="w-48 mt-3 h-1 bg-primary/20 overflow-hidden rounded-full">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: '100%',
                              animation: 'indeterminateProgress 0.7s infinite linear',
                              transformOrigin: '0% 50%',
                            }}
                          />
                        </div>
                        <style jsx>{`
                          @keyframes indeterminateProgress {
                            0% {
                              transform: translateX(0) scaleX(0);
                            }
                            40% {
                              transform: translateX(0) scaleX(0.4);
                            }
                            100% {
                              transform: translateX(100%) scaleX(0.9);
                            }
                          }
                        `}</style>
                      </div>
                    )}

                    {/* Loading Overlay for Asset Loading - No Image State */}
                    {isLoadingAsset && (
                      <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-lg z-10">
                        <div className="flex flex-col items-center gap-4">
                          <svg
                            className="animate-spin h-12 w-12 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <p className="text-white text-sm font-medium">Loading image...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {/* Before-After Preview (non-headshots tools only; headshots has strip above upload) */}
              {toolInfo.id !== 'ai-headshots' && (
                <BeforeAfterPreview toolInfo={toolInfo} />
              )}

              {toolInfo.id === 'ai-headshots' && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setShowPhotoGuideModal(true)}
                    className="px-5 py-3 text-sm font-medium text-primary bg-white border-2 border-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
                  >
                    <span>How to get best results</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center w-full h-full min-h-0">
              {/* Gallery or single-result layout (same fill contract) */}
              {isGalleryEnabled ? (
                <div className="relative w-full h-full min-h-0 max-h-full flex flex-col">
                  <GalleryCarousel
                    galleryImages={galleryImages || []}
                    currentGalleryIndex={currentGalleryIndex || 0}
                    setCurrentGalleryIndex={setCurrentGalleryIndex!}
                    isProcessing={isProcessing}
                    originalImage={originalImage}
                    showPrevious={showPrevious}
                    getPreviousImage={getPreviousImage}
                    getCurrentDisplayedImage={getCurrentDisplayedImage}
                    savedImageIds={savedImageIds}
                    onSaveComplete={(fileId, saved) => {
                      if (setSavedImageIds) {
                        setSavedImageIds(prev => {
                          const newSet = new Set(prev);
                          if (saved) {
                            newSet.add(fileId);
                          } else {
                            newSet.delete(fileId);
                          }
                          return newSet;
                        });
                      }
                    }}
                    onEditWithChat={
                      canEditWithAi ? () => setShowAiEditorModal(true) : undefined
                    }
                  />
                  {/* Loading Overlay for Asset Loading - Gallery Mode */}
                  {isLoadingAsset && (
                    <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-lg z-10">
                      <div className="flex flex-col items-center gap-4">
                        <svg
                          className="animate-spin h-12 w-12 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <p className="text-white text-sm font-medium">Loading image...</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Non-gallery result viewer — same fit/zoom/save model as GalleryCarousel */
                <div className="w-full h-full min-h-0 flex flex-col items-center">
                  {(() => {
                    const displaySrc =
                      showPrevious && getPreviousImage()
                        ? getPreviousImage()!
                        : getCurrentDisplayedImage() || "";
                    const showPills = !!processedImage && !isProcessing;

                    const renderPills = (
                      onOverlayClick?: (e: React.MouseEvent) => void
                    ) => {
                      if (!showPills) return null;
                      return (
                        <div
                          className="flex items-center justify-center gap-2"
                          onClick={onOverlayClick}
                        >
                          <button
                            type="button"
                            onClick={() => displaySrc && setZoomSrc(displaySrc)}
                            className={galleryActionPillClassName}
                            title="Zoom"
                            aria-label="Zoom image"
                          >
                            <svg
                              className="w-4 h-4 shrink-0"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M21 21l-5.2-5.2M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15zM10.5 7v7M7 10.5h7"
                              />
                            </svg>
                            <span>Zoom</span>
                          </button>
                          {canEditWithAi &&
                            (!isHistoryEnabled
                              ? true
                              : historyHook.history.currentIndex > 0) && (
                              <button
                                type="button"
                                onClick={() => setShowAiEditorModal(true)}
                                className={galleryActionPillClassName}
                                title="Edit with Chat"
                                aria-label="Edit with Chat"
                              >
                                <svg
                                  className="w-4 h-4 shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 3v18"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 3l8 4.5-8 4.5-8-4.5L12 3z"
                                  />
                                </svg>
                                <span>AI Edit</span>
                              </button>
                            )}
                          {processedImageFileId && (
                            <SaveButton
                              fileId={processedImageFileId}
                              initialSaved={
                                savedImageIds?.has(processedImageFileId) || false
                              }
                              onSaveComplete={(saved) => {
                                if (setSavedImageIds && processedImageFileId) {
                                  setSavedImageIds((prev) => {
                                    const next = new Set(prev);
                                    if (saved) next.add(processedImageFileId);
                                    else next.delete(processedImageFileId);
                                    return next;
                                  });
                                }
                              }}
                            />
                          )}
                        </div>
                      );
                    };

                    return (
                      <>
                        {showPills && (
                          <div className="hidden md:flex flex-shrink-0 w-full justify-center pt-0 pb-2 z-20">
                            {renderPills()}
                          </div>
                        )}

                        <div className="relative w-full flex-1 min-h-0 flex items-center justify-center">
                          <div
                            className="relative w-full h-full min-h-0 max-w-4xl overflow-hidden"
                            data-image-container
                          >
                            <div className="absolute inset-0 flex items-center justify-center min-h-0 p-1">
                              {showPills && (
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 md:hidden">
                                  {renderPills((e) => e.stopPropagation())}
                                </div>
                              )}
                              {displaySrc ? (
                                // Native img (same as gallery) — fills stage without forced square/letterbox ghosts
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={displaySrc}
                                  alt="Result"
                                  className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg shadow-2xl cursor-pointer"
                                  draggable={false}
                                  onClick={() =>
                                    processedImage && setZoomSrc(displaySrc)
                                  }
                                />
                              ) : null}
                              {isLoadingAsset && (
                                <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-lg z-10">
                                  <div className="flex flex-col items-center gap-4">
                                    <svg
                                      className="animate-spin h-12 w-12 text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                    <p className="text-white text-sm font-medium">
                                      Loading image...
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="hidden md:flex flex-shrink-0 py-1 min-h-[1.75rem] w-full justify-end">
                          {displaySrc && !isProcessing ? (
                            <ImageDimensions imageData={displaySrc} />
                          ) : (
                            <div
                              className="text-gray-300 text-xs mt-2 mr-4 font-mono"
                              aria-hidden
                            >
                              — × —px
                            </div>
                          )}
                        </div>

                        <GalleryZoomModal
                          src={zoomSrc}
                          onClose={() => setZoomSrc(null)}
                        />
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Remove Watermark — only when relevant; no reserved empty strip */}
              {uqf === 1 &&
                ((isGalleryEnabled && galleryImages && galleryImages.length > 0) || processedImage) && (
                  <div className="mt-3 flex items-center justify-center">
                    <Link
                      href="/dashboard/pricing"
                      className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Remove Watermark
                    </Link>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Processing Overlay - ONLY for non-gallery tools (ai-designer only) */}
        {isProcessing && !isLoadingRefineImage && !isGalleryEnabled && (
          <ProcessingOverlay
            isProcessing={isProcessing}
            error={null}
            hasEnhancement={enhancementEnabled || false}
            onErrorDismiss={() => {}}
          />
        )}

        {/* Error Modal - For ALL tools */}
        {error && !isLoadingRefineImage && (
          <div className="absolute inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Generation Failed</h3>
                  <p className="text-sm text-gray-600">{error}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setError(null)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Downloading Overlay */}
        {isDownloading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 shadow-xl flex items-center space-x-4">
              <svg
                className="animate-spin h-6 w-6 text-primary"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-gray-900 font-medium">Downloading...</span>
            </div>
          </div>
        )}

        {/* Refine Image Loading Overlay */}
        {isLoadingRefineImage && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
            <div className="text-center max-w-sm mx-auto p-6">
              {/* Spinner */}
              <svg
                className="animate-spin h-16 w-16 text-white mx-auto mb-6"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  className="opacity-75"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  d="M12 2a10 10 0 0110 10"
                />
              </svg>

              {/* Loading Text */}
              <p className="text-white text-lg font-medium mb-2">
                Loading image for refinement...
              </p>

              {/* Subtext */}
              <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2">
                <svg className="w-4 h-4 text-white mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3l8 4.5-8 4.5-8-4.5L12 3z" />
                </svg>
                <span className="text-white text-sm">
                  Preparing AI Designer
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Clear Image Confirmation Modal */}
        {showClearConfirmModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Clear Image?</h3>
                  <p className="text-sm text-gray-600">
                    {isGalleryEnabled && galleryImages && galleryImages.length > 0
                      ? `This will clear your uploaded image and all generated results.`
                      : `Are you sure you want to remove the current photo and start over? This will clear your uploaded image${processedImage ? ' and generated result' : ''}.`
                    }
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowClearConfirmModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Clear all images
                    setOriginalImage(null);
                    setProcessedImageInternal?.(null);
                    setError(null);
                    // Clear entire history
                    historyHook.clearHistory();
                    // Clear gallery for dev tools
                    if (isGalleryEnabled && clearGallery) {
                      await clearGallery();
                    }
                    // Close modal
                    setShowClearConfirmModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Clear Image
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Warning Modal */}
        {showNavigationWarning && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Don't Lose Your Image!</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    You have an unsaved image. Please save or download it before leaving, or you'll lose your work.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowNavigationWarning(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Stay Here
                </button>
                <button
                  onClick={() => {
                    setShowNavigationWarning(false);
                    setHasDownloadedImage(true);
                    router.push('/dashboard');
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Leave Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Editor Navigation Modal */}
        {showAiEditorModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Continue to AI Editor?</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Load and open your current image into the AI Chat Editor? Unsaved images will be lost.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAiEditorModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Get the fileId to pass to AI Designer
                    const fileId = (window as any).__lastGeneratedFileId || (window as any).__uploadedFileId;

                    if (!fileId) {
                      setError('No image to refine. Please upload or generate an image first.');
                      return;
                    }

                    // Set flag to bypass beforeunload - must happen synchronously
                    sessionStorage.setItem('_bypass_warning', 'true');
                    setShowAiEditorModal(false);

                    // Use Next.js router (no page reload!)
                    router.push(`/editor/ai-designer?refineFileId=${fileId}&fromTool=${toolInfo.id}`);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors shadow-lg"
                >
                  Continue to AI Editor
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Free User Conversion Modal */}
        <FreeUserConversionModal
          isOpen={showFreeUserModal}
          onClose={() => setShowFreeUserModal(false)}
          toolName={toolInfo.title}
        />

        {/* Photo Guide Modal */}
        <PhotoGuideModal
          isModalOpen={showPhotoGuideModal}
          setIsModalOpen={setShowPhotoGuideModal}
        />

      </div>
  );
}
